import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Homepage from './pages/Homepage';
import SearchResults from './pages/SearchResults';
import ListingDetail from './pages/ListingDetail';
import Checkout from './pages/Checkout';
import BookingConfirmation from './pages/BookingConfirmation';
import ClientDashboard from './pages/ClientDashboard';
import ClientProfile from './pages/ClientProfile';
import ClientFavorites from './pages/ClientFavorites';
import ClientKYC from './pages/ClientKYC';
import SubmitReview from './pages/SubmitReview';
import ProviderDashboard from './pages/ProviderDashboard';
import ProviderAssets from './pages/ProviderAssets';
import ProviderAvailability from './pages/ProviderAvailability';
import PostAsset from './pages/PostAsset';
import ProviderBookings from './pages/ProviderBookings';
import ProviderKYC from './pages/ProviderKYC';
import ProviderPayout from './pages/ProviderPayout';
import AdminDashboard from './pages/AdminDashboard';
import AdminBookings from './pages/AdminBookings';
import AdminUsers from './pages/AdminUsers';
import ProviderVerification from './pages/ProviderVerification';
import AdminClientVerification from './pages/AdminClientVerification';
import AdminServiceReview from './pages/AdminServiceReview';
import DisputeManagement from './pages/DisputeManagement';
import CommissionConfig from './pages/CommissionConfig';
import AdminReviews from './pages/AdminReviews';
import AdminCategories from './pages/AdminCategories';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Terminos from './pages/Terminos';
import Privacidad from './pages/Privacidad';
import Cancelacion from './pages/Cancelacion';
import SobreNosotros from './pages/SobreNosotros';
import Contacto from './pages/Contacto';
import Blog from './pages/Blog';
import Seguridad from './pages/Seguridad';
import Entrega from './pages/Entrega';
import { AuthProvider, useAuth } from './context/AuthContext';
import BottomNav from './components/BottomNav';
import Footer from './components/Footer';
import SoporteChat from './components/SoporteChat';
import CookieBanner from './components/CookieBanner';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import ProvidersLanding from './pages/ProvidersLanding';
import './i18n';
import './App.css';

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  // Admins pueden acceder a cualquier sección protegida (cliente / proveedor / admin)
  if (requiredRole && user.role !== requiredRole && user.role !== 'admin') return <Navigate to="/" />;
  return children;
};

function AppRouter() {
  const location = useLocation();

  // Al navegar a otra ruta, posicionar la página al inicio (evita mantener scroll de la anterior)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Públicas */}
        <Route path="/" element={<Homepage />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/buscar" element={<Navigate to="/search" replace />} />
        <Route path="/catalogo" element={<Navigate to="/search" replace />} />
        <Route path="/listing/:assetId" element={<ListingDetail />} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/booking-confirmation" element={<BookingConfirmation />} />
        <Route path="/proveedores" element={<ProvidersLanding />} />
        <Route path="/providers" element={<Navigate to="/proveedores" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Informativas nuevas (RedMobility) */}
        <Route path="/seguridad" element={<Seguridad />} />
        <Route path="/entrega" element={<Entrega />} />

        {/* Legales + corporativas */}
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/cancelacion" element={<Cancelacion />} />
        <Route path="/sobre-nosotros" element={<SobreNosotros />} />
        <Route path="/contacto" element={<Contacto />} />
        <Route path="/blog" element={<Blog />} />

        {/* Cliente */}
        <Route path="/dashboard" element={<Navigate to="/client/dashboard" replace />} />
        <Route path="/verificacion" element={<ProtectedRoute><ClientKYC /></ProtectedRoute>} />
        <Route path="/client/dashboard" element={<ProtectedRoute requiredRole="client"><ClientDashboard /></ProtectedRoute>} />
        <Route path="/client/profile" element={<ProtectedRoute requiredRole="client"><ClientProfile /></ProtectedRoute>} />
        <Route path="/client/verificacion" element={<ProtectedRoute requiredRole="client"><ClientKYC /></ProtectedRoute>} />
        <Route path="/client/favorites" element={<ProtectedRoute requiredRole="client"><ClientFavorites /></ProtectedRoute>} />
        <Route path="/client/review/:bookingId" element={<ProtectedRoute requiredRole="client"><SubmitReview /></ProtectedRoute>} />

        {/* Proveedor */}
        <Route path="/provider/dashboard" element={<ProtectedRoute requiredRole="provider"><ProviderDashboard /></ProtectedRoute>} />
        <Route path="/provider/assets" element={<ProtectedRoute requiredRole="provider"><ProviderAssets /></ProtectedRoute>} />
        <Route path="/provider/assets/:assetId/availability" element={<ProtectedRoute requiredRole="provider"><ProviderAvailability /></ProtectedRoute>} />
        <Route path="/provider/post-asset" element={<ProtectedRoute requiredRole="provider"><PostAsset /></ProtectedRoute>} />
        <Route path="/provider/listings/new" element={<Navigate to="/provider/post-asset" />} />
        <Route path="/provider/bookings" element={<ProtectedRoute requiredRole="provider"><ProviderBookings /></ProtectedRoute>} />
        <Route path="/provider/verificacion" element={<ProtectedRoute requiredRole="provider"><ProviderKYC /></ProtectedRoute>} />
        <Route path="/provider/pagos" element={<ProtectedRoute requiredRole="provider"><ProviderPayout /></ProtectedRoute>} />

        {/* Admin */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
        <Route path="/admin/dashboard" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/verification" element={<ProtectedRoute requiredRole="admin"><ProviderVerification /></ProtectedRoute>} />
        <Route path="/admin/disputes" element={<ProtectedRoute requiredRole="admin"><DisputeManagement /></ProtectedRoute>} />
        <Route path="/admin/commission" element={<ProtectedRoute requiredRole="admin"><CommissionConfig /></ProtectedRoute>} />
        <Route path="/admin/commissions" element={<Navigate to="/admin/commission" />} />
        <Route path="/admin/reviews" element={<ProtectedRoute requiredRole="admin"><AdminReviews /></ProtectedRoute>} />
        <Route path="/admin/categories" element={<ProtectedRoute requiredRole="admin"><AdminCategories /></ProtectedRoute>} />
        <Route path="/admin/bookings" element={<ProtectedRoute requiredRole="admin"><AdminBookings /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute requiredRole="admin"><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/clients/kyc" element={<ProtectedRoute requiredRole="admin"><AdminClientVerification /></ProtectedRoute>} />
        <Route path="/admin/servicios" element={<ProtectedRoute requiredRole="admin"><AdminServiceReview /></ProtectedRoute>} />
        <Route path="/admin/assets" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>
      {(location.pathname === "/" || location.pathname === "/proveedores") && (
        <SoporteChat />
      )}
    </AnimatePresence>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
        <AuthProvider>
          <AppRouter />
          <Footer />
          <BottomNav />
          <CookieBanner />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
